import React, { useEffect, useRef, useState, useCallback } from 'react';
import { api } from './api';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { Swords } from 'lucide-react';
import './App.css';
import PolicyPage from './pages/PolicyPage';
import { POLICY_NAV } from './data/policies';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ConfirmEmailPage from './pages/ConfirmEmailPage';
import AppLayout from './pages/app/AppLayout';
import FeedPage from './pages/app/FeedPage';
import ChatPage from './pages/app/ChatPage';
import NotificationsPage from './pages/app/NotificationsPage';
import ProfilePage from './pages/app/ProfilePage';
import TeamsPage from './pages/app/TeamsPage';
import BattlePage from './pages/app/BattlePage';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

// ── Protected route ─────────────────────────────────────────────
function RequireAuth({ children }) {
  const { token } = useAuth();
  const location = useLocation();
  if (!token) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

// ── Landing page (unchanged) ─────────────────────────────────────

const FEATURES = [
  { num: '01', tag: 'Vote', title: 'Pick your side.', desc: 'Two teams. One battle. Cast your vote and move the needle in real time — every boost counts.', color: '#0EA5E9' },
  { num: '02', tag: 'Live', title: 'Watch it unfold.', desc: 'Rivalries play out live. Percentages shift by the second. The crowd decides who wins.', color: '#EF4444' },
  { num: '03', tag: 'Earn', title: 'Rise through the ranks.', desc: 'Rack up credits with every vote. Build streaks. Your loyalty shows on the leaderboard.', color: '#F97316' },
  { num: '04', tag: 'Connect', title: 'Find your fan zone.', desc: 'Every team has its crowd. Jump in, talk trash, celebrate wins with fans who bleed the same colors.', color: '#FBBF24' },
];


function HomePage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { token } = useAuth();
  const [storeLinks, setStoreLinks] = useState({ google_play_url: '', app_store_url: '' });
  const [comingSoonToast, setComingSoonToast] = useState(false);

  useEffect(() => {
    api.getAppStoreLinks().then(setStoreLinks).catch(() => {});
  }, []);

  const handleStoreClick = (e, url) => {
    if (!url) {
      e.preventDefault();
      setComingSoonToast(true);
      setTimeout(() => setComingSoonToast(false), 3000);
    }
  };

  useEffect(() => {
    document.title = 'RivalCam — Pick a Side. Vote. Win.';
    return () => {};
  }, []);


  return (
    <div className="rc-root">
      {comingSoonToast && (
        <div className="store-coming-toast">
          🔗 Direct link dropping soon — the rivalry is already live!
        </div>
      )}
      <nav className="rc-nav">
        <div className="rc-nav-inner">
          <a href="#hero" className="rc-logo-wrap">
            <img src="/rivalogo.png" alt="RivalCam" className="rc-nav-logo-img" />
            <span className="rc-logo-text">RIVALCAM</span>
          </a>
          <div className={`rc-nav-links ${menuOpen ? 'open' : ''}`}>
            <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
            <a href="#download" onClick={() => setMenuOpen(false)} className="rc-nav-download">Download</a>
            <a href="https://meanc.net" onClick={() => setMenuOpen(false)}>MEANC</a>
            {token && (
              <Link to="/app/feed" className="rc-nav-cta" onClick={() => setMenuOpen(false)}>Open App</Link>
            )}
            {/* Sign In — re-enable when ready
            {!token && <Link to="/login" className="rc-nav-cta" onClick={() => setMenuOpen(false)}>Sign In</Link>}
            */}
          </div>
          <button className="rc-hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      <section className="rc-hero" id="hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/hero2.png)` }}>
        <div className="orb orb-1" /><div className="orb orb-2" /><div className="orb orb-3" />
        <img src="/rivalogo.png" alt="" className="rc-hero-watermark" aria-hidden="true" />
        <div className="rc-hero-content">
          <div className="rc-hero-text">
            <img src="/rivalogo.png" alt="RivalCam" className="rc-hero-logo" />
            <div className="rc-badge"><span className="rc-badge-dot" /><span>Live Rivalries Happening Now</span></div>
            <h1 className="rc-headline">Pick a&nbsp;Side.<br /><span className="rc-headline-accent">Vote.</span><br /><span className="rc-headline-fire">Win.</span></h1>
            <p className="rc-subhead">Two teams. One rivalry. Rally your squad, vote for your crew, and watch the live battle unfold. It's a group chat — with actual stakes.</p>
            {/* CTA buttons — re-enable when ready
            <div className="rc-hero-cta-group">
              <Link to="/signup" className="rc-hero-cta-btn rc-hero-cta-primary">
                ⚡ Get Started Free
              </Link>
              <Link to="/login" className="rc-hero-cta-btn rc-hero-cta-secondary">
                Sign In
              </Link>
            </div>
            */}
            <div className="rc-store-btns">
              <a href={storeLinks.google_play_url || '#'} onClick={(e) => handleStoreClick(e, storeLinks.google_play_url)} className="rc-store-btn rc-google" target="_blank" rel="noopener noreferrer">
                <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76a2 2 0 0 1-1.01-.92 2.8 2.8 0 0 1-.36-1.46V2.62a2.8 2.8 0 0 1 .36-1.46A2 2 0 0 1 3.18.24L13.8 12 3.18 23.76Zm11.26-6.55-2.6-2.6 1.85-1.86 4.2 2.38a1.3 1.3 0 0 1 0 2.26l-4.2 2.38-1.85-1.86 2.6-2.6ZM4.4 1.06l8.43 8.43-2.37 2.37L4.4 1.06Zm0 21.88 6.06-10.8 2.37 2.37L4.4 22.94Z"/></svg>
                <div className="store-text"><span className="store-sub">Get it on</span><span className="store-main">Google Play</span></div>
              </a>
              <a href={storeLinks.app_store_url || '#'} onClick={(e) => handleStoreClick(e, storeLinks.app_store_url)} className="rc-store-btn rc-apple" target="_blank" rel="noopener noreferrer">
                <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                <div className="store-text"><span className="store-sub">Download on the</span><span className="store-main">App Store</span></div>
              </a>
            </div>
            <div className="rc-social-proof">
              <div className="rc-avatars">
                {['🏈','⚽','🏀','🎾','🏒'].map((e, i) => (<div key={i} className="rc-avatar" style={{ zIndex: 5 - i }}>{e}</div>))}
              </div>
              <span className="rc-proof-text"><strong>50,000+</strong> fans already in the game</span>
            </div>
          </div>
        </div>
        <div className="rc-scroll-hint"><div className="scroll-mouse"><div className="scroll-dot" /></div><span>Scroll to explore</span></div>
      </section>


      <section className="rc-how">
        <div className="rc-section-inner">
          <p className="rc-eyebrow">How It Works</p>
          <h2 className="rc-section-title">Three steps to the rivalry</h2>
          <div className="rc-steps">
            {[
              { n: '01', title: 'Join a Rivalry', desc: 'Browse live and upcoming matchups across football, basketball, soccer and more.' },
              { n: '02', title: 'Vote Your Side', desc: 'Pick your team, share the battle, and watch your squad\'s votes move the needle in real time.' },
              { n: '03', title: 'Celebrate the Win', desc: 'See the final result, earn credits, and carry the bragging rights into the next battle.' },
            ].map((step) => (
              <div key={step.n} className="rc-step">
                <div className="rc-step-num">{step.n}</div>
                <div><h3 className="rc-step-title">{step.title}</h3><p className="rc-step-desc">{step.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

<section className="rc-features" id="features">
        <div className="rc-section-inner">
          <p className="rc-eyebrow">Features</p>
          <h2 className="rc-section-title">Built for fans who mean it</h2>
          <div className="rc-feat-list">
            {FEATURES.map((f) => (
              <div key={f.num} className="rc-feat-row">
                <div className="rc-feat-left">
                  <span className="rc-feat-num" style={{ color: f.color }}>{f.num}</span>
                  <span className="rc-feat-tag" style={{ color: f.color, borderColor: f.color }}>{f.tag}</span>
                </div>
                <div className="rc-feat-divider" style={{ background: f.color }} />
                <div className="rc-feat-body">
                  <h3 className="rc-feat-title">{f.title}</h3>
                  <p className="rc-feat-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rc-cta" id="download">
        <div className="cta-orb-1" /><div className="cta-orb-2" />
        <div className="rc-cta-inner">
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
            <img src="/rivalogo.png" alt="RivalCam" className="rc-cta-logo" />
          </div>
          <h2 className="rc-cta-title">Your rivalry is waiting.</h2>
          <p className="rc-cta-sub">Download RivalCam free. Pick your side and start the battle today.</p>
          {/* Get Started Free — re-enable when ready
          <Link to="/signup" className="rc-hero-cta-btn rc-hero-cta-primary" style={{ display: 'inline-flex', marginBottom: '24px' }}>
            ⚡ Get Started Free
          </Link>
          */}
          <div className="rc-store-btns rc-store-btns--center rc-store-btns--hero">
            <a href={storeLinks.google_play_url || '#'} onClick={(e) => handleStoreClick(e, storeLinks.google_play_url)} className="rc-store-btn rc-google rc-store-btn--highlight" target="_blank" rel="noopener noreferrer">
              <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76a2 2 0 0 1-1.01-.92 2.8 2.8 0 0 1-.36-1.46V2.62a2.8 2.8 0 0 1 .36-1.46A2 2 0 0 1 3.18.24L13.8 12 3.18 23.76Zm11.26-6.55-2.6-2.6 1.85-1.86 4.2 2.38a1.3 1.3 0 0 1 0 2.26l-4.2 2.38-1.85-1.86 2.6-2.6ZM4.4 1.06l8.43 8.43-2.37 2.37L4.4 1.06Zm0 21.88 6.06-10.8 2.37 2.37L4.4 22.94Z"/></svg>
              <div className="store-text"><span className="store-sub">Get it on</span><span className="store-main">Google Play</span></div>
            </a>
            <a href={storeLinks.app_store_url || '#'} onClick={(e) => handleStoreClick(e, storeLinks.app_store_url)} className="rc-store-btn rc-apple rc-store-btn--highlight" target="_blank" rel="noopener noreferrer">
              <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              <div className="store-text"><span className="store-sub">Download on the</span><span className="store-main">App Store</span></div>
            </a>
          </div>
        </div>
      </section>

      <footer className="rc-footer">
        <div className="rc-footer-top">
          <div className="rc-footer-brand-block">
            <div className="rc-footer-brand">
              <Swords size={32} color="#0EA5E9" />
              <div><strong>RIVALCAM</strong><small>by MEANC Technologies</small></div>
            </div>
            <p className="rc-footer-tagline">Live fan rivalry — pick your side, vote, and celebrate the win.</p>
            <a className="rc-footer-cta" href="mailto:services@meanc.net">Talk to us</a>
          </div>
          <div className="rc-footer-grid">
            <div className="rc-footer-col">
              <p className="rc-footer-heading">Product</p>
              <a href="#features">Features</a>
              <a href="#download">Download</a>
              <a href="https://meanc.net" target="_blank" rel="noreferrer">MEANC</a>
            </div>
            <div className="rc-footer-col">
              <p className="rc-footer-heading">Company</p>
              <Link to="/info/career">Career</Link>
              <Link to="/info/help-center">Help Center</Link>
              <Link to="/info/forum">Forum</Link>
            </div>
            <div className="rc-footer-col">
              <p className="rc-footer-heading">Legal</p>
              <Link to="/info/privacy">Privacy Policy</Link>
              <Link to="/info/terms">Terms & Conditions</Link>
              <Link to="/info/cookies">Cookies Policy</Link>
              <Link to="/info/copyright">Copyright Policy</Link>
            </div>
            <div className="rc-footer-col">
              <p className="rc-footer-heading">Contact</p>
              <a href="mailto:support@meanc.net">support@meanc.net</a>
              <a href="mailto:services@meanc.net">services@meanc.net</a>
            </div>
          </div>
        </div>
        <div className="rc-footer-bottom">
          <p>© {new Date().getFullYear()} MEANC Technologies – All Rights Reserved</p>
          <div className="rc-footer-bottom-links">
            {POLICY_NAV.map((link) => (<Link key={link.slug} to={`/info/${link.slug}`}>{link.label}</Link>))}
          </div>
          <Swords size={24} color="#0EA5E9" style={{ opacity: 0.6 }} />
        </div>
      </footer>
    </div>
  );
}

// ── Root App ──────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public landing */}
          <Route path="/" element={<HomePage />} />
          <Route path="/info/:slug" element={<PolicyPage />} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Protected app */}
          <Route path="/app" element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }>
            <Route index element={<Navigate to="/app/feed" replace />} />
            <Route path="feed" element={<FeedPage />} />
            <Route path="teams" element={<TeamsPage />} />
            <Route path="battle/:id" element={<BattlePage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
